import React, { useState, useRef, useEffect } from 'react';
import { downloadStoryAsPDF, getSupportedMimeType, startRecording } from './StoryPlayerUtils';
import { showStyledAlert } from './StyledAlert';

// Props: media (array), storyTitle (string)
export default function StoryPlayer({
  media = [], storyTitle = '', onPracticeTalking = null, practiceLabel = 'Scenario Practice',
}) {
  const resolvedPracticeLabel = typeof practiceLabel === 'string' && practiceLabel.trim()
    ? practiceLabel
    : 'Scenario Practice';
  // Guard: If no media or page is invalid, show message and do not reference hooks or 'page'
  if (!media || media.length === 0) {
    return <div className="text-center text-gray-400 p-10">Loading...</div>;
  }

  // All hooks must come after the guard
  const [currentPage, setCurrentPage] = useState(0);
  const [autoplay, setAutoplay] = useState(false);
  const [editMode, setEditMode] = useState(false);
  // Controls panel toggle state
  const [showControls, setShowControls] = useState(false);
  const [editedStory, setEditedStory] = useState(null);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedBlobs, setRecordedBlobs] = useState([]);
  const [pageAudios, setPageAudios] = useState(() => media.map(p => p.audio || null));
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioSpeed, setAudioSpeed] = useState(1);
  const audioRef = useRef(null);
  //const autoplayTimeout = useRef(null);
  const pageRef = useRef(null);
  const allPagesRef = useRef(null);

  // Effects
  useEffect(() => {
    setPageAudios(media.map(p => p.audio || null));
  }, [media]);

  // Autoplay effect: play audio when page changes or autoplay is toggled
  useEffect(() => {
    if (autoplay && audioRef.current && !recording) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
    // eslint-disable-next-line
  }, [currentPage, autoplay]);
  // Insert a virtual title page as the first page
  const totalPages = (media?.length || 0) + 1;
  const isTitlePage = currentPage === 0;
  // For content pages, map currentPage (1-based) to media[currentPage-1]
  const page = !isTitlePage && media && media.length > 0 && currentPage - 1 >= 0 && currentPage - 1 < media.length ? media[currentPage - 1] : null;
  // Guard: If no media, show loading
  if (!media || media.length === 0) {
    return <div className="text-center text-gray-400 p-10">Loading...</div>;
  }


  // Update pageAudios if media changes
  useEffect(() => {
    setPageAudios(media.map(p => p.audio || null));
  }, [media]);

  // When audio ends, advance if autoplay
  const handleAudioEnded = () => {
    setIsPlaying(false);
    if (autoplay && currentPage < media.length) {
      setCurrentPage((prev) => prev + 1);
    }
  };


  // Recording logic using utility
  const handleStartRecording = async () => {
    try {
      const pageIndex = Math.max(0, currentPage - 1);
      const { recorder } = await startRecording((blob, mimeType) => {
        const url = URL.createObjectURL(blob);
        setPageAudios(prev => {
          const arr = [...prev];
          if (currentPage > 0 && pageIndex < arr.length) {
            arr[pageIndex] = url;
          }
          return arr;
        });
        setRecordedBlobs([]);
        setRecording(false);
      });
      setMediaRecorder(recorder);
      setRecordedBlobs([]);
      setRecording(true);
    } catch (err) {
      showStyledAlert('Microphone access denied or error: ' + err, { severity: 'error' });
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setMediaRecorder(null);
    }
  };



  const handleToggleAutoplay = (e) => {
    e.stopPropagation();

    // If user presses Play on the cover/title page, jump to the first story page
    // so audio + illustration start immediately.
    if (currentPage === 0 && !autoplay) {
      setCurrentPage(1);
    }

    setAutoplay((a) => {
      const next = !a;
      if (!next && audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
      }
      return next;
    });
  };

  const changeAudioSpeed = (delta) => {
    setAudioSpeed((prev) => {
      const next = Math.min(2, Math.max(0.5, +(prev + delta).toFixed(2)));
      return next;
    });
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = audioSpeed;
    }
  }, [audioSpeed, currentPage]);

  // Wait for all images to load before exporting PDF
  const handleDownloadPDF = async () => {
    const container = allPagesRef.current;
    if (!container) return downloadStoryAsPDF(editedStory?.title || storyTitle, allPagesRef);
    // Chrome can capture fallback-font metrics before the selected fonts finish
    // loading, which causes words and punctuation to overlap in html2canvas.
    if (document.fonts?.ready) {
      await document.fonts.ready;
    }
    const images = Array.from(container.querySelectorAll('img'));
    await Promise.all(images.map(img => {
      if (img.complete && img.naturalWidth !== 0) return Promise.resolve();
      return new Promise(resolve => {
        img.onload = img.onerror = resolve;
      });
    }));
    downloadStoryAsPDF(editedStory?.title || storyTitle, allPagesRef);
  };
  const paletteColors = ['#ffffff', '#fdf6e3', '#fef3c7', '#EDF1DF', '#fce7f3', '#E3E8DC', '#fff7ed'];
  const [paletteIndex, setPaletteIndex] = useState(3);
  const handleCyclePalette = () => {
    setPaletteIndex((prev) => (prev + 1) % paletteColors.length);
  };

  const pageWrapperStyle = {
    backgroundColor: paletteColors[paletteIndex],
    minHeight: '100vh',
    width: '100%',
    padding: '0',
  };

  const handleEditToggle = () => {
    setEditMode((prev) => !prev);
  };
  const handleStoryChange = (e, field) => {
    setEditedStory({ ...editedStory, [field]: e.target.value });
  };

  return (
    <div style={pageWrapperStyle}>
      {/* Hidden container for all pages PDF export */}
      <div ref={allPagesRef} style={{ position: 'absolute', left: '-9999px', top: 0, width: '1120px', zIndex: -1, pointerEvents: 'none', background: '#fff' }}>
        {/* PDF Cover Page */}
        <div className="pdf-story-page pdf-story-cover-page" style={{ boxSizing: 'border-box', width: '1120px', height: '792px', padding: '36px', marginBottom: '24px', background: '#fff', border: '8px solid #f1f5f9', borderRadius: '24px', display: 'flex', flexDirection: 'row', alignItems: 'stretch' }}>
          <div style={{ boxSizing: 'border-box', width: '50%', height: '100%', padding: '12px 24px 12px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {media[0]?.img && (
              <img src={media[0].img} crossOrigin="anonymous" alt={media[0].text?.slice(0, 20) || 'Story cover illustration'} style={{ display: 'block', width: '500px', height: '500px', maxWidth: '100%', borderRadius: '16px', background: '#EDF1DF', objectFit: 'contain' }} />
            )}
          </div>
          <div style={{ boxSizing: 'border-box', width: '50%', height: '100%', padding: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <h1 style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '42px', lineHeight: 1.2, fontWeight: 'bold', fontKerning: 'none', letterSpacing: '0.2px', wordSpacing: '2px', color: '#555', textAlign: 'left', margin: '0', width: '100%' }}>{editedStory?.title ?? storyTitle}</h1>
          </div>
        </div>
        {/* PDF Content Pages (no title) */}
        {media.map((page, idx) => (
          <div key={idx} className="pdf-story-page" style={{ boxSizing: 'border-box', width: '1120px', height: '792px', padding: '36px', marginBottom: '24px', background: '#fff', border: '1px solid #eee', borderRadius: '16px', display: 'flex', flexDirection: 'row', alignItems: 'stretch', position: 'relative', overflow: 'hidden' }}>
            <div style={{ boxSizing: 'border-box', width: '50%', height: '100%', padding: '12px 24px 12px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src={page.img} crossOrigin="anonymous" alt={page.text?.slice(0, 20) || 'Story illustration'} style={{ display: 'block', width: '500px', height: '500px', maxWidth: '100%', borderRadius: '16px', background: '#EDF1DF', objectFit: 'contain' }} />
            </div>
            <div style={{ boxSizing: 'border-box', width: '50%', height: '100%', padding: '44px 42px 58px', display: 'flex', alignItems: 'center' }}>
              {/* No title on content pages */}
              <div style={{ width: '100%', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '26px', lineHeight: 1.45, fontKerning: 'none', fontVariantLigatures: 'none', letterSpacing: '0.15px', wordSpacing: '2px', color: '#333', whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>{(editedStory?.pages?.[idx]?.text ?? page.text)}</div>
            </div>
            <div style={{ position: 'absolute', right: '40px', bottom: '32px', color: '#888', fontSize: '0.85rem' }}>{idx + 1} / {media.length}</div>
          </div>
        ))}
      </div>
      <div ref={pageRef} className="page-container max-w-6xl w-full bg-white rounded-2xl page-shadow overflow-hidden transition-all duration-300 cursor-pointer mx-auto flex flex-col justify-center items-center min-h-[24rem] " style={{ border: '8px solid #f1f5f9' }}>
        {/* Cover page: image left, title right, vertically centered */}
        {isTitlePage ? (
          <div className="flex flex-col md:flex-row min-h-[24rem] w-full items-center">
            <div className="w-full md:w-1/2 h-full flex-shrink-0 flex items-center justify-center p-4 md:p-0">
              {media[0]?.img && (
                <img
                  src={media[0].img}
                  alt={media[0].text?.slice(0, 20) || 'Story cover illustration'}
                  style={{ width: '100%', maxWidth: '100%', minHeight: '320px', height: '100%', borderRadius: '1rem', background: '#EDF1DF', objectFit: 'cover' }}
                />
              )}
            </div>
            <div className="w-full md:w-1/2 p-6 md:p-8 lg:p-10 flex flex-col justify-center border-t md:border-t-0 md:border-l border-transparent relative" style={{ minHeight: '18rem' }}>
              {editMode ? (
                <input
                  className="story-title text-gray-500 text-4xl md:text-5xl font-bold w-full border-b border-gray-300 focus:outline-none text-center md:text-left"
                  value={editedStory?.title ?? storyTitle}
                  onChange={e => setEditedStory({ ...editedStory, title: e.target.value })}
                  style={{ lineHeight: '1.2' }}
                />
              ) : (
                <h1 className="story-title text-gray-500 text-3xl md:text-5xl font-bold text-center md:text-left" style={{ lineHeight: '1.2' }}>{editedStory?.title ?? storyTitle}</h1>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row min-h-[24rem] w-full items-stretch">
            {/* Image */}
            <div className="w-full md:w-1/2 h-full flex-shrink-0 flex items-center justify-center p-4 md:p-0">
              <img
                src={page.img}
                alt={page.text?.slice(0, 20) || 'Story illustration'}
                style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', borderRadius: '1rem', background: '#EDF1DF', objectFit: 'cover' }}
              />
            </div>
            {/* Text & Audio */}
            <div className="w-full md:w-1/2 p-6 md:p-8 lg:p-10 flex flex-col justify-center border-t md:border-t-0 md:border-l border-transparent relative">
              <div className="story-text text-gray-500 text-2xl md:text-3xl space-y-4 flex flex-col justify-center">
                {editMode ? (
                  <textarea
                    className="w-full border rounded p-2 text-xl"
                    value={editedStory?.pages?.[currentPage - 1]?.text ?? page.text}
                    onChange={e => {
                      const newPages = Array.isArray(editedStory?.pages) ? [...editedStory.pages] : Array.isArray(media) ? media.map(p => ({ ...p })) : [];
                      newPages[currentPage - 1] = {
                        ...newPages[currentPage - 1],
                        text: e.target.value,
                      };
                      setEditedStory({ ...editedStory, pages: newPages });
                    }}
                    rows={6}
                  />
                ) : (
                  (editedStory?.pages?.[currentPage - 1]?.text ?? page.text).split('\n').map((line, idx) => (
                    <p key={idx}>{line}</p>
                  ))
                )}
              </div>
              <audio
                ref={audioRef}
                src={pageAudios[currentPage - 1] || page.audio}
                style={{ display: 'none' }}
                onEnded={handleAudioEnded}
                onPause={() => setIsPlaying(false)}
                onPlay={() => setIsPlaying(true)}
                onError={e => {
                  // eslint-disable-next-line no-console
                  console.error('Audio failed to load:', e, 'src:', pageAudios[currentPage - 1] || page.audio);
                }}
              />
              {currentPage === media.length && onPracticeTalking && !editMode && (
                <button
                  type="button"
                  className="mt-8 self-center rounded-full bg-[#274D3B] px-6 py-3 text-base font-bold text-white shadow-md transition hover:bg-[#1F3D30] focus:outline-none focus:ring-2 focus:ring-[#6F9654] focus:ring-offset-2"
                  onClick={(event) => {
                    event.stopPropagation();
                    onPracticeTalking({
                      title: editedStory?.title || storyTitle,
                      description: (editedStory?.pages || media).map((item) => item?.text || '').filter(Boolean).join(' '),
                    });
                  }}
                >
                  {resolvedPracticeLabel}
                </button>
              )}
              {/* Recording button moved to footer */}
              <span className="block w-full text-center text-base text-gray-500 select-none mt-6 mb-0 md:mt-0 md:mb-0 md:absolute md:left-0 md:bottom-0 md:pb-2">
                {currentPage} / {media.length}
              </span>
            </div>
          </div>
        )}
        {/* Controls Footer - Main Navigation + Toggle Controls */}
        <div className="p-4 border-t border-transparent text-center text-sm text-gray-500 bg-transparent rounded-b-xl flex justify-center gap-2 items-center">
          {/* Main navigation buttons */}
          <button
            className="bg-transparent px-3 py-2 rounded"
            title="Start Over"
            onClick={() => setCurrentPage(0)}
            disabled={currentPage === 0}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width:'1.5em',height:'1.5em',display:'inline-block',verticalAlign:'middle'}}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m18.75 4.5-7.5 7.5 7.5 7.5m-6-15L5.25 12l7.5 7.5" />
            </svg>
          </button>
          <button
            className="bg-transparent px-3 py-2 rounded"
            title="Previous"
            onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
            disabled={currentPage === 0}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width:'1.5em',height:'1.5em',display:'inline-block',verticalAlign:'middle'}}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            className="ml-2 bg-transparent hover:bg-gray-300 text-gray-500 font-bold py-2 px-4 rounded-lg transition duration-150 focus:outline-none"
            title={autoplay ? "Disable Autoplay" : "Enable Autoplay"}
            style={{ outline: autoplay ? '2px solid #3A6752' : undefined }}
            onClick={handleToggleAutoplay}
          >
            {autoplay ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width:'1.5em',height:'1.5em',display:'inline-block',verticalAlign:'middle'}}>
                <rect x="6" y="5" width="4" height="14" rx="1" fill="#3A6752" />
                <rect x="14" y="5" width="4" height="14" rx="1" fill="#3A6752" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width:'1.5em',height:'1.5em',display:'inline-block',verticalAlign:'middle'}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
          <button
            className="bg-transparent px-3 py-2 rounded"
            title="Next"
            onClick={() => setCurrentPage((p) => Math.min(media.length, p + 1))}
            disabled={currentPage === media.length}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width:'1.5em',height:'1.5em',display:'inline-block',verticalAlign:'middle'}}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            className="bg-transparent px-3 py-2 rounded"
            title="End"
            onClick={() => setCurrentPage(media.length)}
            disabled={currentPage === media.length}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width:'1.5em',height:'1.5em',display:'inline-block',verticalAlign:'middle'}}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m5.25 4.5 7.5 7.5-7.5 7.5m6-15 7.5 7.5-7.5 7.5" />
            </svg>
          </button>
          {/* Toggle Controls Button */}
          <button
            id="toggle-controls-btn"
            className="bg-transparent px-3 py-2 rounded"
            title={showControls ? "Hide Controls" : "Show Controls"}
            onClick={() => setShowControls((v) => !v)}
            style={{ marginLeft: '0.5rem' }}
          >
            <span id="toggle-controls-icon">
              {showControls ? (
                // Minus icon
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width:'1.5em',height:'1.5em',display:'inline-block',verticalAlign:'middle'}}>
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="#fef3c7" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h8" />
                </svg>
              ) : (
                // Plus icon
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width:'1.5em',height:'1.5em',display:'inline-block',verticalAlign:'middle'}}>
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="#fef3c7" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h8M12 8v8" />
                </svg>
              )}
            </span>
          </button>
        </div>

        {/* Controls Panel - Hidden unless toggled */}
        {showControls && (
          <div className="p-4 md:p-6 bg-gray-100 flex flex-col gap-2 border-t border-gray-200 mt-2 rounded-b-xl items-center">
            <div className="flex flex-col md:flex-row justify-center items-center gap-3 w-full max-w-3xl">
              <div className="inline-flex flex-wrap justify-center items-center gap-2">
                <button
                  onClick={handleCyclePalette}
                  title="Change background color"
                  style={{
                    width: '40px',
                    height: '40px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: paletteColors[paletteIndex],
                    border: '2px solid #999',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
                    padding: 0
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.18)';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.12)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: '22px', height: '22px', color: '#666' }}>
                    <circle cx="12" cy="12" r="10" />
                    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                    <circle cx="9" cy="9" r="1.5" fill="currentColor" />
                    <circle cx="15" cy="9" r="1.5" fill="currentColor" />
                  </svg>
                </button>
                <button
                  className={`px-4 py-2 rounded ${editMode ? 'bg-yellow-500 text-white' : 'bg-gray-200'}`}
                  onClick={handleEditToggle}
                >
                  {editMode ? 'Save' : 'Edit'}
                </button>
                {recording ? (
                  <button
                    className="bg-red-500 text-white px-4 py-2 rounded"
                    title="Stop Recording"
                    onClick={handleStopRecording}
                  >
                    ⏹️
                  </button>
                ) : (
                  <button
                    className="bg-green-500 text-white px-4 py-2 rounded"
                    title="Record Audio"
                    onClick={handleStartRecording}
                  >
                    🎤
                  </button>
                )}
                <button
                  className="px-4 py-2 rounded bg-[#274D3B] hover:bg-[#1F3D30] text-white"
                  onClick={() => handleDownloadPDF()}
                >
                  📥
                </button>
              </div>
              <div className="inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white px-2 py-1 shadow-sm">
                <span className="text-xs font-semibold text-gray-700">Speed</span>
                <button
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-gray-50 text-base text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => changeAudioSpeed(-0.25)}
                  disabled={audioSpeed <= 0.5}
                  title="Decrease speed"
                >
                  –
                </button>
                <span className="min-w-[2.5rem] text-center text-xs font-semibold text-gray-800 bg-gray-100 px-2 py-1 rounded-full">
                  {audioSpeed.toFixed(2)}x
                </span>
                <button
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-gray-50 text-base text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => changeAudioSpeed(0.25)}
                  disabled={audioSpeed >= 2}
                  title="Increase speed"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="w-full border-t border-gray-100 bg-gray-50 px-4 py-3 text-center text-sm text-gray-500">
          <a
            href="https://stories.marstrix.com"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="font-semibold text-green-600 hover:text-green-700 hover:underline focus:outline-none focus:ring-2 focus:ring-green-400 rounded px-1"
            aria-label="Open stories.marstrix.com in a new tab"
          >
            stories.marstrix.com
          </a>
        </div>
      </div>
    </div>
  );
}
